import { getQOQText } from '@op/utils/time';
import type { ServerTimeType } from '@op/components/FilterCommon/types';
import {
  DEFAULT_QUICK_KEYS,
  getCountriesFromQuickKeys,
  TCountryMode,
  TXDimension,
} from './constants';
import { loadCountryPrefs } from './utils';
import type { IXHashtagApiFilter, IXHashtagSubFilter } from './types';

export type TDateFilter = {
  start_time?: string;
  end_time?: string;
  date_type?: ServerTimeType;
} | null;

function getCountriesFromPrefs(
  prefs: NonNullable<ReturnType<typeof loadCountryPrefs>>,
): string[] {
  if (prefs.countryMode === 'custom') {
    return prefs.customCountries;
  }
  const keys =
    prefs.activeQuickKeys.length > 0
      ? prefs.activeQuickKeys
      : [...DEFAULT_QUICK_KEYS];
  return getCountriesFromQuickKeys(keys);
}

export function getInitialSubFilter(): IXHashtagSubFilter {
  const prefs = loadCountryPrefs();
  if (prefs) {
    // custom + []：全部国家，API 不传 countries 字段
    if (prefs.countryMode === 'custom') {
      return {
        countries: prefs.customCountries,
        group_by: 'date',
        search_text: '',
      };
    }

    const countries = getCountriesFromPrefs(prefs);
    if (countries.length > 0) {
      return {
        countries,
        group_by: 'date',
        search_text: '',
      };
    }
  }

  return {
    countries: getCountriesFromQuickKeys([...DEFAULT_QUICK_KEYS]),
    group_by: 'date',
    search_text: '',
  };
}

export function getInitialCountryMode(): TCountryMode {
  return loadCountryPrefs()?.countryMode ?? 'quick';
}

export function getInitialActiveQuickKeys(): string[] {
  const prefs = loadCountryPrefs();
  if (!prefs) {
    return [...DEFAULT_QUICK_KEYS];
  }

  // custom：展示自定义选中国家，快捷区无选中项（activeQuickKeys 仍保存在 prefs 供切回 quick）
  if (prefs.countryMode === 'custom') {
    return [];
  }

  return prefs.activeQuickKeys.length > 0
    ? prefs.activeQuickKeys
    : [...DEFAULT_QUICK_KEYS];
}

export function getInitialCustomCountries(): string[] {
  return loadCountryPrefs()?.customCountries ?? [];
}

/** 单日优先于单国：返回禁用项 + 需要强制切换的 group_by */
export function getMetricsConstraints(
  subFilter: IXHashtagSubFilter,
  dateFilter: TDateFilter,
) {
  const isSingleCountry = subFilter.countries.length === 1;

  let isSingleDate = false;
  if (dateFilter?.start_time) {
    const dateType = dateFilter.date_type ?? 'daily';
    isSingleDate = getQOQText(
      dateType,
      dateFilter.start_time,
      dateFilter.end_time || dateFilter.start_time,
    ).isSingleDate;
  }

  if (isSingleDate) {
    return {
      canByDate: false,
      canByCountry: true,
      forcedGroupBy: 'country' as TXDimension,
    };
  }

  if (isSingleCountry) {
    return {
      canByDate: true,
      canByCountry: false,
      forcedGroupBy: 'date' as TXDimension,
    };
  }

  return {
    canByDate: true,
    canByCountry: true,
    forcedGroupBy: undefined,
  };
}

export function buildApiFilter(
  dateFilter: TDateFilter,
  subFilter: IXHashtagSubFilter,
): IXHashtagApiFilter | null {
  if (!dateFilter?.start_time) return null;

  const payload: IXHashtagApiFilter = {
    start_time: dateFilter.start_time,
    end_time: dateFilter.end_time || dateFilter.start_time,
    group_by: subFilter.group_by,
  };

  if (subFilter.countries.length > 0) {
    payload.countries = subFilter.countries;
  }
  if (subFilter.search_text) {
    payload.search_text = subFilter.search_text;
  }

  return payload;
}

export function getNextActiveQuickKeys(
  countryMode: TCountryMode,
  activeQuickKeys: string[],
  key: string,
): string[] {
  if (countryMode === 'custom') {
    return [key];
  }

  if (!activeQuickKeys.includes(key)) {
    return [...activeQuickKeys, key];
  }

  // 至少保留一项，不能全部取消
  if (activeQuickKeys.length === 1) {
    return activeQuickKeys;
  }

  return activeQuickKeys.filter(k => k !== key);
}
